import { Grid, Paper, Typography } from "@mui/material";
import { filter, find, map, some } from "lodash";
import { useContext, useState } from "react";
import { EssealDataTable } from "esseal-data-table"
import { useMutation, useQuery } from "@apollo/client/react";
import equipmentRegisterColumns from "./equipmentRegisterColumns";
import EquipmentRegisterToolbar from "./Toolbar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { grey } from "@mui/material/colors";
import DeliveryIcon from "@/assets/DeliveryIcon";
import { ReportContext } from "@/Contexts/ReportContext";
import { MarkAsReadyForCollectionDocument, GetSchedulesDocument, CompanyResourceResourceTypeEnum, UpdateScheduleDocument, PermissionActionEnum, PermissionEntityEnum, ResourceScheduleStatusEnum, GetDeliveryItemStatesDocument, Schedule } from "@/graphql/generated/graphql";
import { StyledRegisterHead } from "@/theme/styled-components";
import { displayNotification } from "@/utils/notifications";
import PermissionContext from "@/Contexts/Permissions";
import NewScheduleModal from "@/components/Scheduling/NewScheduleModal";
import EditScheduleModal from "@/components/Scheduling/EditScheduleModal";
import CurrentUserContext from "@/Contexts/CurrentUser";

const EquipmentRegister: React.FC = () => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { selectedProject, selectedReport, projectShifts } = useContext(ReportContext);
  const { can } = useContext(PermissionContext);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(
    null
  );

  const [markAsReadyForCollection] = useMutation(
    MarkAsReadyForCollectionDocument,
    {
      onCompleted: () => {
        displayNotification({
          text: "Equipment marked as ready for collection successfully",
          type: "success",
        });
      },
      onError: (error) => {
        displayNotification({ text: error.message, type: "error" });
      },
      refetchQueries: [
        {
          query: GetSchedulesDocument,
          variables: {
            projectId: selectedReport?.projectId,
            date: selectedReport?.reportDate,
            resourceType: CompanyResourceResourceTypeEnum.Equipment,
          },
        },
      ],
    }
  );

  const { data, loading } = useQuery(GetSchedulesDocument, {
    skip: !selectedReport,
    variables: {
      projectId: selectedReport?.projectId as string,
      date: selectedReport?.reportDate as string,
      resourceType: CompanyResourceResourceTypeEnum.Equipment
    },
  });

  const equipmentSchedules = filter(data?.getSchedules || [], (schedule) =>
    schedule.companyResource?.resourceType === CompanyResourceResourceTypeEnum.Equipment
  );

  const [update] = useMutation(UpdateScheduleDocument);

  const handleAddFunction = () => {
    if (!can(PermissionActionEnum.Create, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;

    setOpenModal(true);
  };

  const handleEditFunction = (id: string) => {
    if (!can(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;

    setSelectedEquipmentId(id);
  };

  const handleDeleteFunction = (id: string) => {
    if (!can(PermissionActionEnum.Manage, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;
    if (!selectedReport) return;

    update({
      variables: {
        schedule: {
          id,
          status: ResourceScheduleStatusEnum.Dropped,
        }
      },
      update(cache) {
        cache.evict({
          id: cache.identify({ __typename: "Schedule", id }),
        });
        cache.gc();
      },
      onCompleted: () =>
        displayNotification({
          type: "success",
          text: "Equipment schedule removed successfully",
        }),
      onError: (error) =>
        displayNotification({ type: "error", text: error.message }),
    });
  };

  const handleReadyForCollectionFunction = (id: string, companyResourceId: string) => {
    if (!can(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;

    markAsReadyForCollection({
      variables: {
        scheduleId: id,
        companyResourceId,
        projectId: selectedReport?.projectId as string,
        reportId: selectedReport?.id as string,
      },
      update(cache, { data }) {
        const updatedState = data?.markAsReadyForCollection;
        const reportId = selectedReport?.id;

        if (!reportId || !updatedState) return;

        const existingItems =
          cache.readQuery({
            query: GetDeliveryItemStatesDocument,
            variables: { reportId },
          })?.getDeliveryItemStates ?? [];

        cache.writeQuery({
          query: GetDeliveryItemStatesDocument,
          variables: { reportId },
          data: {
            getDeliveryItemStates: some(
              existingItems,
              (item) => item.item.id === updatedState.item.id
            )
              ? map(existingItems, (item) => (item.item.id === updatedState.item.id ? updatedState : item))
              : [...existingItems, updatedState],
          },
        });
      }
    })
  };

  if (!selectedReport) return <></>;

  const selectedSchedule = find(equipmentSchedules, (schedule) => schedule.id === selectedEquipmentId)

  return (
    <Grid component={Paper} mb={2} size={12}>
      <StyledRegisterHead>
        <Typography variant="caption" fontWeight={700}>
          Equipment Register
        </Typography>
      </StyledRegisterHead>
      <EssealDataTable
        height={320}
        rows={data?.getSchedules as Schedule[] || []}
        columns={equipmentRegisterColumns(dateTimePreference.time as string)}
        loading={loading}
        maxVisibleActions={3}
        toolbar={<EquipmentRegisterToolbar onAddRecord={handleAddFunction} />}
        rowActions={(row) => [
          {
            label: "Edit",
            icon: <EditIcon fontSize="small" />,
            tooltip: "Edit Schedule",
            onClick: (row) => handleEditFunction(row.id)
          },
          {
            label: "Delete",
            icon: <DeleteIcon fontSize="small" />,
            tooltip: "Delete Schedule",
            onClick: (row) => handleDeleteFunction(row.id)
          },
          {
            label: "Ready for Collection",
            icon: <DeliveryIcon sx={{ fontSize: "22px" }} color={row.collectable ? "#ED6C02" : grey[400]} />,
            tooltip: row.collectable ? "Ready for Collection" : "",
            disabled: !row.collectable,
            onClick: () => handleReadyForCollectionFunction(row.id, row.companyResource?.id as string)
          }
        ]}

      />

      {openModal &&
        <NewScheduleModal
          addresses={selectedProject?.addresses || []}
          resourceType={CompanyResourceResourceTypeEnum.Equipment}
          reportDate={selectedReport.reportDate}
          projectId={selectedProject?.id as string}
          reportId={selectedReport?.id as string}
          projectShifts={projectShifts}
          handleClose={() => setOpenModal(false)}
        />
      }

      {selectedEquipmentId && selectedSchedule && (
        <EditScheduleModal
          handleClose={() => setSelectedEquipmentId(null)}
          selectedSchedule={selectedSchedule}
        />
      )}
    </Grid>
  );
};

export default EquipmentRegister;
